import { IBaseModel } from '../base/base-model.interface';
import { createEnumOptions } from '../shared/enum-option.interface';

export enum AdminState {
  active = 'active',
  inactive = 'inactive',
}

export enum OperateStatus {
  up = 1,
  down = 2,
  invalid = -1,
}

export const adminStateOptions = createEnumOptions({
  [AdminState.active]: {
    label: 'Aktívny',
    color: 'success',
    icon: 'check',
  },
  [AdminState.inactive]: {
    label: 'Neaktívny',
    color: 'danger',
    icon: 'exclamation',
  },
  _default: {
    label: '-',
    color: 'none',
  },
});

export const operateStatusOptions = createEnumOptions({
  [OperateStatus.up]: {
    label: 'Online',
    color: 'success',
    icon: 'check',
  },
  [OperateStatus.down]: {
    label: 'Offline',
    color: 'danger',
    icon: 'times',
  },
  [OperateStatus.invalid]: {
    label: '-',
    color: 'none',
  },
  _default: {
    label: '-',
    color: 'none',
  },
});

export enum OntActions {
  activate = 1,
  deactivate = 2,
  restart = 3,
  forceVas = 4,
  factoryReset = 5,
}

export enum OntRunStatusEnum {
  Up = 'Up',
  Down = 'Down',
}

export const ontRunStatusOptions = createEnumOptions({
  [OntRunStatusEnum.Up]: {
    label: 'Online',
    color: 'success',
    icon: 'check',
  },
  [OntRunStatusEnum.Down]: {
    label: 'Offline',
    color: 'danger',
    icon: 'times',
  },
  _default: {
    label: '-',
    color: 'none',
  },
});

export enum OntGraphKey {
  inTraffic = 'inTraffic',
  outTraffic = 'outTraffic',
  temperature = 'temperature',
  oltRxPower = 'oltRxPower',
  transceiverVoltage = 'transceiverVoltage',
  transceiverCurrent = 'transceiverCurrent',
  transceiverTemperature = 'transceiverTemperature',
  duplexMode = 'duplexMode',

  ponIfVoltage = 'ponIfVoltage',
  ponIfTxPower = 'ponIfTxPower',
  ponIfTemperature = 'ponIfTemperature',
  ponIfBias = 'ponIfBias',
  ponIfHCInOctets = 'ponIfHCInOctets',
  ponIfHCOutOctets = 'ponIfHCOutOctets',
  ponIfFecStatus = 'ponIfFecStatus',

  configStatus = 'configStatus',
  alarmState = 'alarmState',
  ddmVoltage = 'ddmVoltage',
  ddmCurrent = 'ddmCurrent',
  ddmTemperature = 'ddmTemperature',

  ontTxPower = 'ontTxPower',
  ontRxPower = 'ontRxPower',

  ontFecRxCorrected = 'ontFecRxCorrected',
  ontFecRxUncorrected = 'ontFecRxUncorrected',
  ontFecTotal = 'ontFecTotal',
  range = 'range',

  lastDownCause = 'lastDownCause',
  lastDownTime = 'lastDownTime',
  lastUpTime = 'lastUpTime',

  ethState = 'ethState',
  ethAdminState = 'ethAdminState',
  ethSpeed = 'ethSpeed',
}

export const defaultGraphKeys = [
  OntGraphKey.inTraffic,
  OntGraphKey.outTraffic,
  OntGraphKey.temperature,
  OntGraphKey.oltRxPower,
  OntGraphKey.ontRxPower,
  OntGraphKey.ethSpeed,
];

export enum ZabbixOntItemKey {
  runStatus = 'ont.hwGponDeviceOntControlRunStatus',
  configStatus = 'ont.hwGponDeviceOntControlConfigStatus',

  alarmState = 'ont.hwGponDeviceOntObjAlarmStateRDIi',
  ddmVoltage = 'ont.hwGponOntOpticalDdmVoltage',
  ddmCurrent = 'ont.hwGponOntOpticalDdmBiasCurrent',
  ddmTemperature = 'ont.hwGponOntOpticalDdmTemperature',

  ontTxPower = 'ont.hwGponOntOpticalDdmTxPower',
  ontRxPower = 'ont.hwGponOntOpticalDdmRxPower',

  ontFecRxCorrected = 'ont.hwGponOntTrafficFlowStatisticUpFecDecodCorrectCnt',
  ontFecRxUncorrected = 'ont.hwGponOntTrafficFlowStatisticUpFecDecodUnCorrectCnt',
  ontFecTotal = 'ont.hwGponOntPerfCurr15MinFecTotalCodeWords',

  duplexMode = 'ont\\.hwGponDeviceOntEthernetDuplexMode\\.\\[\\d+\\]',

  range = 'ont.hwGponDeviceOntControlRanging',

  lastDownCause = 'ont.hwGponDeviceOntControlLastDownCause',
  lastDownTime = 'ont.hwGponDeviceOntControlLastDownTime',
  lastUpTime = 'ont.hwGponDeviceOntControlLastUpTime',
  inTraffic = 'ont.hwGponOntStatisticDownbps',
  outTraffic = 'ont.hwGponOntStatisticUpbps',
  temperature = 'ont.opticalModuleTemperature',
  oltRxPower = 'ont.hwGponOntOpticalDdmOltRxOntPower',

  ethState = 'ont\\.hwGponDeviceOntEthernetOnlineState\\.\\[\\d+\\]',
  ethAdminState = 'ont\\.hwGponDeviceOntEthernetOperateStatus\\.\\[\\d+\\]',
  ethSpeed = 'ont\\.hwGponDeviceOntEthernetSpeed\\.\\[\\d+\\]',
}

export interface OntRunStatus {
  runstat?: OntRunStatusEnum;
  adminstat: OntRunStatusEnum;
  batterystat?: string;
  lstdowntime?: Date | '--';
  lstdowncause?: string;
  lineprof: string;
  servprof: string;
}

export interface IOnt extends IBaseModel<'ONT'> {
  groupId?: string;
  sn: string;
  name?: string;
  operateState?: OperateStatus;
  adminStatus?: AdminState;
  lastDowntime?: Date;
  lastUptime?: Date;
  uptime?: number;
  downCause?: string;
  productName?: string;
  range?: number;
  eic?: string;
  gpsX?: number;
  gpsY?: number;
  fttxId?: string;
  fsp?: string;
  ontId?: number;
  oltName?: string;
  frame?: number;
  slot?: number;
  port?: number;
  softwareVersion?: string;
  lineProfile?: string;
  serviceProfile?: string;
  vasProfile?: string;
  oltRx?: number;
  ontRx?: number;
  temp?: string;
  terminalType?: string;
  activated?: boolean;
  shouldForceVas: boolean;
  conflict?: boolean;
}

export enum OntPortAdminState {
  up = 'Up',
  down = 'Down',
}

export const ontPortAdminStateOptions = createEnumOptions({
  [OntPortAdminState.up]: {
    label: 'UP',
    color: 'success',
  },
  [OntPortAdminState.down]: {
    label: 'DOWN',
    color: 'danger',
  },
  _default: {
    label: '-',
    color: 'none',
  },
});

export enum OntPortState {
  acive = 'Active',
  activating = 'Activating',
}

export const ontPortStateOptions = createEnumOptions({
  [OntPortState.acive]: {
    label: 'Active',
    color: 'success',
  },
  [OntPortState.activating]: {
    label: 'Activating',
    color: 'warn',
  },
  _default: {
    label: '-',
    color: 'none',
  },
});

export const zabbixDownCausesMap = new Map<string, string>([
  ['1', 'LOS(Loss of signal)'],
  ['2', 'LOSi(Loss of signal for ONUi) or LOBi (Loss of burst for ONUi)'],
  ['3', 'LOFI(Loss of frame of ONUi)'],
  ['4', 'SFI(Signal fail of ONUi)'],
  ['5', 'LOAI(Loss of acknowledge with ONUi)'],
  ['6', 'LOAMI(Loss of PLOAM for ONUi)'],
  ['7', 'deactive ONT fails'],
  ['8', 'deactive ONT success'],
  ['9', 'reset ONT'],
  ['10', 're-register ONT'],
  ['11', 'pop up fail'],
  ['13', 'dying-gasp'],
  ['15', 'LOKI(Loss of key synch with ONUi)'],
  ['18', 'deactived ONT due to the ring'],
  ['30', 'shut down ONT optical module'],
  ['31', 'reset ONT by ONT command'],
  ['32', 'reset ONT by ONT reset button'],
  ['33', 'reset ONT by ONT software'],
  ['34', 'deactived ONT due to broadcast attack'],
  ['35', 'operator check fail'],
  ['37', 'a rogue ONT detected by itself'],
  ['-1', 'query fails'],
]);

export const ontGraphKeysOptions = createEnumOptions<OntGraphKey>({
  [OntGraphKey.inTraffic]: { label: 'In traffic' },
  [OntGraphKey.outTraffic]: { label: 'Out traffic' },
  [OntGraphKey.temperature]: { label: 'Temperature' },
  [OntGraphKey.oltRxPower]: { label: 'OLT RX Power' },
  [OntGraphKey.transceiverVoltage]: { label: 'Transceiver Voltage' },
  [OntGraphKey.transceiverCurrent]: { label: 'Transceiver Current' },
  [OntGraphKey.transceiverTemperature]: { label: 'Transceiver Temperature' },
  [OntGraphKey.duplexMode]: { label: 'Duplex Mode' },
  [OntGraphKey.ponIfVoltage]: { label: 'PON Interface Voltage' },
  [OntGraphKey.ponIfTxPower]: { label: 'PON Interface TX Power' },
  [OntGraphKey.ponIfTemperature]: { label: 'PON Interface Temperature' },
  [OntGraphKey.ponIfBias]: { label: 'PON Interface Bias' },
  [OntGraphKey.ponIfHCInOctets]: { label: 'PON Interface HC In Octets' },
  [OntGraphKey.ponIfHCOutOctets]: { label: 'PON Interface HC Out Octets' },
  [OntGraphKey.ponIfFecStatus]: { label: 'PON Interface FEC Status' },
  [OntGraphKey.configStatus]: { label: 'Config Status' },
  [OntGraphKey.alarmState]: { label: 'Alarm State' },
  [OntGraphKey.ddmVoltage]: { label: 'DDM Voltage' },
  [OntGraphKey.ddmCurrent]: { label: 'DDM Current' },
  [OntGraphKey.ddmTemperature]: { label: 'DDM Temperature' },
  [OntGraphKey.ontTxPower]: { label: 'ONT TX Power' },
  [OntGraphKey.ontRxPower]: { label: 'ONT RX Power' },
  [OntGraphKey.ontFecRxCorrected]: { label: 'ONT FEC RX Corrected' },
  [OntGraphKey.ontFecRxUncorrected]: { label: 'ONT FEC RX Uncorrected' },
  [OntGraphKey.ontFecTotal]: { label: 'ONT FEC Total' },
  [OntGraphKey.range]: { label: 'Range' },
  [OntGraphKey.lastDownCause]: { label: 'Last Down Cause' },
  [OntGraphKey.lastDownTime]: { label: 'Last Down Time' },
  [OntGraphKey.lastUpTime]: { label: 'Last Up Time' },
  [OntGraphKey.ethState]: { label: 'ETH State' },
  [OntGraphKey.ethAdminState]: { label: 'ETH Admin State' },
  [OntGraphKey.ethSpeed]: { label: 'ETH Speed' },
  _default: { label: '-' },
});

export enum RstType {
  default = '1',
  complete = '2',
}

export const rstTypeOptions = createEnumOptions<RstType>({
  [RstType.default]: { label: 'Default' },
  [RstType.complete]: { label: 'Complete' },
  _default: { label: '-' },
});

export interface IOntActionData {
  action: OntActions;
  rsttype?: RstType;
}
